
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { CategorySelector } from "@/components/common/CategorySelector";
import { Undo } from "lucide-react";
import type { RentalProduct } from "@/types/entities";
import type { LastAction } from "@/domains/orders/hooks/types";
import { useInventoryCategories } from "@/hooks/useCategoriesUnified";
import { useIsCompact } from "@/hooks/use-compact";

type InventorySelectionSectionProps = {
  selectedItems: Array<{ id: string; quantity: number }>;
  availableInventoryItems: RentalProduct[];
  inventoryLoading: boolean;
  lastAction?: LastAction | null;
  onInventoryItemChange: (id: string, quantity: number) => void;
  onUndoLastAction: () => void;
  readOnly?: boolean;
}

export const InventorySelectionSection: React.FC<InventorySelectionSectionProps> = ({
  selectedItems,
  availableInventoryItems,
  inventoryLoading,
  lastAction,
  onInventoryItemChange,
  onUndoLastAction,
  readOnly = false
}) => {
  // null означает "Все категории"
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const { categories, loading: categoriesLoading } = useInventoryCategories();
  const isCompact = useIsCompact();
  
  // Функция для получения названия категории по ID
  const getCategoryName = (categoryId: string | undefined): string => {
    if (!categoryId) return 'Без категории';
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Неизвестная категория';
  };

  // Фильтрация по выбранной категории (точное совпадение ID). null => все
  const filteredItems = selectedCategoryId === null
    ? availableInventoryItems
    : availableInventoryItems?.filter(item => item.category === selectedCategoryId);

  const getItemQuantity = (itemId: string): number => {
    const item = selectedItems.find(i => i.id === itemId);
    return item ? item.quantity : 0;
  };

  // Show loading state only for inventory loading; allow categories to load lazily
  if (inventoryLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Инвентарь для аренды</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2">Загрузка инвентаря...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={isCompact ? "space-y-3" : "flex items-center justify-between gap-3"}>
        <h3 className="text-lg font-medium">Инвентарь для аренды</h3>
        <div className={isCompact ? "flex flex-col gap-2" : "flex items-center gap-2"}>
          <div className={isCompact ? "w-full" : "w-64"}>
            <CategorySelector
              value={selectedCategoryId ?? undefined}
              onValueChange={(val) => setSelectedCategoryId(val || null)}
              categoryType="inventory"
              placeholder="Все категории"
              hierarchical
              disabled={categoriesLoading}
            />
          </div>
          {lastAction && !readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={onUndoLastAction}
              className={isCompact ? "w-full" : "text-sm"}
            >
              <Undo className="h-4 w-4 mr-1" />
              Отменить
            </Button>
          )}
        </div>
      </div>

      {!availableInventoryItems || availableInventoryItems.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>Нет доступного инвентаря для данной точки аренды</p>
          <p className="text-xs mt-2">Проверьте настройки точки аренды или добавьте инвентарь</p>
        </div>
      ) : (
        <div className="w-full">
          <div className="space-y-2">
            {!filteredItems || filteredItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>
                  {selectedCategoryId === null
                    ? 'Нет доступного инвентаря'
                    : `В выбранной категории нет доступного инвентаря`}
                </p>
              </div>
            ) : (
              filteredItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <span className="font-medium">{item.name}</span>
                    <div className="text-sm text-muted-foreground">
                      {getCategoryName(item.category)} • Доступно: {item.availableQuantity}
                      {item.dailyPrice && ` • ${item.dailyPrice}₽/день`}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      max={item.availableQuantity}
                      value={getItemQuantity(item.id)}
                      onChange={(e) => onInventoryItemChange(item.id, parseInt(e.target.value) || 0)}
                      disabled={readOnly}
                      aria-disabled={readOnly}
                      className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:text-muted-foreground"
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

